import { generateId, generateJoinCode, publicIdFor } from './codes';
import type { LeaderboardEntry, PlayerState, TripState } from './types';

/**
 * Trip lifecycle. Trips are an optional overlay on lifetime mode, not the main
 * loop - `activeTripId === null` is the default state of the app.
 *
 * A trip is created locally and registered with the server on the next sync, so
 * it can be started and played before the server has heard of it. The shape is
 * the same either way, which is why the server-issued fields are parameters
 * rather than generated inline.
 */

export interface NewTripOptions {
  name: string;
  /** Server-issued when joining; generated locally when creating offline. */
  id?: string;
  joinCode?: string;
  /** True when the server already knows about this trip (i.e. we just joined it). */
  registered?: boolean;
  isOwner?: boolean;
  now?: number;
}

export function newTrip(options: NewTripOptions): TripState {
  const now = options.now ?? Date.now();
  return {
    id: options.id ?? generateId(),
    joinCode: options.joinCode ?? generateJoinCode(),
    name: options.name.trim() || 'Road trip',
    startedAt: now,
    endedAt: null,
    myClaims: {},
    undone: {},
    leaderboard: [],
    leaderboardAt: 0,
    registered: options.registered ?? false,
    isOwner: options.isOwner ?? true,
    version: 0,
  };
}

/** Trips this player started that the server has not been told about yet. */
export function unregisteredTrips(state: PlayerState): TripState[] {
  return state.trips.filter((t) => !t.registered && t.endedAt === null && t.isOwner);
}

/** Owned, registered, finished trips - the ones the server needs told about. */
export function endedOwnedTrips(state: PlayerState): TripState[] {
  return state.trips.filter((t) => t.isOwner && t.registered && t.endedAt !== null);
}

/** Creates a trip and makes it active, so claiming starts crediting it immediately. */
export function startTrip(state: PlayerState, options: NewTripOptions): PlayerState {
  const trip = newTrip(options);
  return {
    ...state,
    trips: [trip, ...state.trips],
    activeTripId: trip.id,
    version: state.version + 1,
  };
}

export function endTrip(state: PlayerState, tripId: string, now = Date.now()): PlayerState {
  const trip = state.trips.find((t) => t.id === tripId);
  if (!trip || trip.endedAt !== null) return state;

  return {
    ...state,
    trips: state.trips.map((t) =>
      t.id === tripId ? { ...t, endedAt: now, version: t.version + 1 } : t,
    ),
    activeTripId: state.activeTripId === tripId ? null : state.activeTripId,
    version: state.version + 1,
  };
}

/** Passing null returns to lifetime mode. Ended trips cannot be reactivated. */
export function setActiveTrip(state: PlayerState, tripId: string | null): PlayerState {
  if (tripId === null) {
    if (state.activeTripId === null) return state;
    return { ...state, activeTripId: null, version: state.version + 1 };
  }

  const trip = state.trips.find((t) => t.id === tripId);
  if (!trip || trip.endedAt !== null || state.activeTripId === tripId) return state;

  return { ...state, activeTripId: tripId, version: state.version + 1 };
}

export function getTrip(state: PlayerState, tripId: string): TripState | undefined {
  return state.trips.find((t) => t.id === tripId);
}

export function activeTrips(state: PlayerState): TripState[] {
  return state.trips.filter((t) => t.endedAt === null);
}

export function pastTrips(state: PlayerState): TripState[] {
  return state.trips.filter((t) => t.endedAt !== null);
}

/** A board row as the server sent it before public ids. */
type LegacyEntry = Omit<LeaderboardEntry, 'publicId'> & { publicId?: string; playerId?: string };

/**
 * Rewrites trip boards saved before public ids, which carried every participant's
 * raw player id - a bearer credential.
 *
 * Each id is swapped for exactly the public id the server now sends, so "you"
 * stays highlighted on finished trips whose boards will never refresh, and other
 * people's credentials stop riding up to the server inside this player's synced
 * payload.
 *
 * Applied wherever a state arrives from outside - IndexedDB, a sync merge, a
 * restore, a sign-in - rather than by a schema migration, because a copy that has
 * not been upgraded yet can come back from the server at any of those. It keys
 * off the legacy field itself, so on a clean state it returns the same object.
 */
export async function upgradeLegacyBoards(state: PlayerState): Promise<PlayerState> {
  const isLegacy = (entry: LegacyEntry) => entry.playerId !== undefined;
  const anyLegacy = state.trips.some((trip) =>
    (trip.leaderboard as LegacyEntry[]).some(isLegacy),
  );
  if (!anyLegacy) return state;

  const upgrade = async (entry: LegacyEntry): Promise<LeaderboardEntry> => {
    const { playerId, publicId, ...rest } = entry;
    return { ...rest, publicId: publicId ?? (await publicIdFor(playerId ?? '')) };
  };

  const trips = await Promise.all(
    state.trips.map(async (trip) => ({
      ...trip,
      leaderboard: await Promise.all((trip.leaderboard as LegacyEntry[]).map(upgrade)),
    })),
  );
  return { ...state, trips };
}
